import WebSocketManager from "@/utils/websocket"
import { STORAGE_KEY, WS_EVENTS, BASE_URL } from "@/api/constants"
import { getWSConnectionTicket } from "@/api/modules/user.api.js"

export const websocket = {
  namespaced: true,

  state: () => ({
    // 连接状态
    connection: {
      connected: false,
      reconnecting: false,
      reconnectAttempts: 0,
      readyState: WebSocket.CLOSED,
      url: process.env.VUE_APP_WS_TARGET,
      defaultConfig: {
        reconnectInterval: 3000,
        maxReconnectAttempts: 10,
        heartbeatInterval: 30000,
        heartbeatTimeout: 5000
      }
    },

    // 消息相关
    messages: {
      list: [], // 消息列表
      lastMessage: null, // 最后一条消息
      unreadCount: 0, // 未读消息数
      queue: [] // 待发送消息队列
    },

    // 会话信息
    session: {
      id: null,
      userId: null,
      startTime: null,
      lastActive: null
    },

    // 统计信息
    stats: {
      messagesSent: 0,
      messagesReceived: 0,
      reconnects: 0,
      errors: 0,
      connectedAt: null,
      disconnectedAt: null
    },

    ticket: null,

    // 错误信息
    error: null
  }),

  mutations: {
    SET_TICKET(state, ticket) {
      // console.group('🎫 ticket 变化');
      // console.log('时间:', new Date().toISOString());
      // console.log('新 ticket:', ticket);
      // console.log('旧 ticket:', state.ticket);
      // console.log('调用栈:', new Error().stack);
      // console.groupEnd();
      state.ticket = ticket
    },
    // 新增：清除 ticket
    CLEAR_TICKET(state) {
      state.ticket = null
    },
    // 设置连接状态
    SET_CONNECTED(state, status) {
      state.connection.connected = status
      state.connection.readyState = status ? WebSocket.OPEN : WebSocket.CLOSED

      if (status) {
        state.stats.connectedAt = Date.now()
        state.stats.disconnectedAt = null
      } else {
        state.stats.disconnectedAt = Date.now()
      }
    },

    // 设置重连状态
    SET_RECONNECTING(state, status) {
      state.connection.reconnecting = status
    },

    // 设置重连尝试次数
    SET_RECONNECT_ATTEMPTS(state, attempts) {
      state.connection.reconnectAttempts = attempts
      if (attempts > 0) {
        state.stats.reconnects++
      }
    },

    // 设置连接 URL
    SET_URL(state, url) {
      state.connection.url = url
    },

    // 添加接收到的消息
    ADD_MESSAGE(state, message) {
      const msg = {
        id:
          message.id ||
          `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        type: message.type,
        payload: message.payload,
        timestamp: message.timestamp || Date.now(),
        direction: "incoming",
        read: false
      }

      state.messages.list.push(msg)
      state.messages.lastMessage = msg

      // 限制消息列表大小
      if (state.messages.list.length > 100) {
        state.messages.list.shift()
      }

      state.messages.unreadCount++
      state.stats.messagesReceived++
    },

    // 添加发送的消息
    ADD_OUTGOING_MESSAGE(state, message) {
      const msg = {
        id:
          message.id ||
          `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        type: message.type,
        payload: message.payload,
        timestamp: Date.now(),
        direction: "outgoing",
        status: "sent"
      }

      state.messages.list.push(msg)
      state.messages.lastMessage = msg

      if (state.messages.list.length > 100) {
        state.messages.list.shift()
      }

      state.stats.messagesSent++
    },

    // 标记消息为已读
    MARK_MESSAGES_AS_READ(state) {
      state.messages.list.forEach(msg => {
        if (!msg.read) msg.read = true
      })
      state.messages.unreadCount = 0
    },

    // 清空消息
    CLEAR_MESSAGES(state) {
      state.messages.list = []
      state.messages.lastMessage = null
      state.messages.unreadCount = 0
    },

    // 添加消息到队列
    ADD_TO_QUEUE(state, message) {
      state.messages.queue.push({
        ...message,
        queuedAt: Date.now()
      })

      // 限制队列大小
      if (state.messages.queue.length > 20) {
        state.messages.queue.shift()
      }
    },

    // 清空队列
    CLEAR_QUEUE(state) {
      state.messages.queue = []
    },

    // 设置会话信息
    SET_SESSION(state, sessionData) {
      state.session = {
        ...state.session,
        ...sessionData
      }
    },

    // 设置会话 ID
    SET_SESSION_ID(state, id) {
      state.session.id = id
    },

    // 设置用户 ID
    SET_USER_ID(state, userId) {
      state.session.userId = userId
      localStorage.setItem(STORAGE_KEY.USER_ID, userId)
    },

    // 更新最后活动时间
    UPDATE_LAST_ACTIVE(state) {
      state.session.lastActive = Date.now()
    },

    // 设置错误
    SET_ERROR(state, error) {
      state.error = error ? error.message || error.toString() : null
      if (error) {
        state.stats.errors++
      }
    },

    // 重置统计
    RESET_STATS(state) {
      state.stats = {
        messagesSent: 0,
        messagesReceived: 0,
        reconnects: 0,
        errors: 0,
        connectedAt: null,
        disconnectedAt: null
      }
    },

    // 重置所有状态
    RESET_STATE(state) {
      // 保留部分配置信息
      const url = state.connection.url
      const userId = state.session.userId

      // 重置所有状态
      state.connection = {
        connected: false,
        reconnecting: false,
        reconnectAttempts: 0,
        readyState: WebSocket.CLOSED,
        url: url
      }

      state.messages = {
        list: [],
        lastMessage: null,
        unreadCount: 0,
        queue: []
      }

      state.session = {
        id: null,
        userId: userId,
        startTime: null,
        lastActive: null
      }

      state.stats = {
        messagesSent: 0,
        messagesReceived: 0,
        reconnects: 0,
        errors: 0,
        connectedAt: null,
        disconnectedAt: null
      }

      state.ticket = ticket
      state.error = null
    }
  },

  actions: {
    async connectWithTicket({ commit, dispatch, state }) {
      console.group('🔌 connectWithTicket 被调用')
      console.log('时间:', new Date().toISOString())
      console.groupEnd()
      try {
        // 如果已经有 ticket 且 WebSocket 已连接，不需要重新获取
        // if (state.ticket && state.connection.connected) {
        //   console.log('已经连接且有 ticket，跳过')
        //   return
        // }

        const { data } = await getWSConnectionTicket()
        // 如果 ticket 没变，也不需要重新初始化
        // if (data === state.ticket) {
        //   console.log('ticket 没变，跳过')
        //   return
        // }

        if (!data) {
          throw new Error("获取ticket失败")
        }

        commit("SET_TICKET", data)
        const wsTarget = process.env.VUE_APP_WS_TARGET
        const baseUrl = wsTarget + "/api/auction/ws"
        console.log('🌐 完整的 baseUrl:', baseUrl)

        const manager = WebSocketManager.getInstance({
          url: baseUrl
        })
        // ✅ 设置事件监听（只在第一次设置）
        if (!manager._listenersSetup) {
          await dispatch("_watchWebSocketEvents", { manager })
          manager._listenersSetup = true
        }

        // ✅ 直接用新 ticket 连接
        manager.connectWithNewTicket(data)
        return manager
        //return await dispatch("initialize")
      } catch (error) {
        console.error("获取ticket失败:", error)
        throw error
      }
    },

    // 初始化 WebSocket 连接
    async initialize({ commit, dispatch, state }, config = {}) {
      try {
        commit("SET_ERROR", null)

        let url = process.env.VUE_APP_WS_TARGET + "/api/auction/ws"
        //ws://52.80.166.149:5888/api/auction/ws?ticket=067fa11c44854ee7b977bc4664d6a88e

        if (state.ticket) {
          const urlObj = new URL(url)
          urlObj.searchParams.append("ticket", state.ticket)
          url = urlObj.toString()
        }

        const finalConfig = {
          url: url,
          reconnectInterval:
            config.reconnectInterval ||
            state.connection.defaultConfig.reconnectInterval,
          maxReconnectAttempts:
            config.maxReconnectAttempts ||
            state.connection.defaultConfig.maxReconnectAttempts,
          heartbeatInterval:
            config.heartbeatInterval ||
            state.connection.defaultConfig.heartbeatInterval,
          heartbeatTimeout:
            config.heartbeatTimeout ||
            state.connection.defaultConfig.heartbeatTimeout
        }

        const manager = WebSocketManager.getInstance(finalConfig)
        manager.reconfigure({ url: url })
        // 设置事件监听
        await dispatch("_watchWebSocketEvents", { manager })

        // 如果未连接，则连接
        if (!manager.isConnected() && !manager.isConnecting()) {
          manager.connect()
        }

        return manager
      } catch (error) {
        commit("SET_ERROR", error)
        throw error
      }
    },

    // 监听 WebSocket 事件（内部方法）
    async _watchWebSocketEvents({ commit, dispatch }, { manager }) {
      if (!window._messageCount) window._messageCount = 0
      // 移除所有旧监听
      manager.listeners.clear()

      // ✅ 监听 need_ticket 事件
      manager.on('need_ticket', () => {
        console.log('🔄 监听到需要新的 ticket，正在获取...')
        dispatch('connectWithTicket')
      })

      manager.on("open", () => {
        console.log('🟢 WebSocket open 事件触发')
        commit("SET_CONNECTED", true)
        commit("SET_RECONNECTING", false)
        commit("SET_RECONNECT_ATTEMPTS", 0)
        commit("SET_SESSION", { startTime: Date.now() })

        // 发送队列中的消息
        // dispatch('flushMessageQueue');

        // 如果已有用户ID，尝试恢复会话
        // const userId = this.state?.user?.auth?.userId || localStorage.getItem(STORAGE_KEY.USER_ID);
        // if (userId) {
        //   commit('SET_USER_ID', userId);
        //   dispatch('resumeSession', userId);
        // }
      })

      manager.on("close", () => {
        commit("SET_CONNECTED", false)
        commit("UPDATE_LAST_ACTIVE")
      })

      manager.on("reconnecting", ({ attempt, max }) => {
        commit("SET_RECONNECTING", true)
        commit("SET_RECONNECT_ATTEMPTS", attempt)
      })

      manager.on("message", data => {
        window._messageCount++
        console.log(
          `[${window._messageCount}] WebSocket message 事件触发, 收到数据:`,
          data
        )
        commit("ADD_MESSAGE", data)
        dispatch("handleMessage", data)
      })

      manager.on("error", error => {
        commit("SET_ERROR", error)
      })

      manager.on("max_reconnect_attempts", () => {
        commit("SET_ERROR", "达到最大重连次数，请检查网络")
      })
    },

    // 处理收到的消息
    handleMessage({ commit, dispatch, rootState }, message) {
      console.log('📨 handleMessage 收到:', JSON.stringify(message))

      if (message.event === WS_EVENTS.PING) {
        dispatch("send", {
          action: WS_EVENTS.PONG,
          payload: Date.now()
        })
        return // 心跳消息不继续往下处理
      }

      switch (message.event) {
        case 'sale_update':
          if (message.channel == 'board') {
            dispatch('liveBigScreen/updateCurrentLotCompletely', message, { root: true })
          } else if (message.channel == 'live') {
            dispatch('liveAuctionState/updateCurrentLotCompletely', message, { root: true })
          } else if (message.channel == 'workbench') {
            let { lastHammerPrice, lastStatus, remainingCount, scheduledCount, sellThroughRate, totalRealisedPrice, ...currentLot } = message.data
            let auctionSummary = { lastHammerPrice, lastStatus, remainingCount, scheduledCount, sellThroughRate, totalRealisedPrice }
            dispatch('liveAdminAuctionState/updateCurrentLotCompletely', currentLot, { root: true })
            dispatch('liveAdminAuctionState/updateAuctionCompletely', auctionSummary, { root: true })
          }
          break
        case 'qualification_update':
          if (message.channel == 'live') {
            dispatch('qualificationState/updateQualificationCompletely', message.data, { root: true })
          }
          break
      }


      return
      // 根据消息类型处理
      switch (message.type) {
        case WS_EVENTS.PING:
          break

        case WS_EVENTS.USER_STATUS:
          // 可以在这里更新 user 模块的状态
          if (rootState.user) {
            // 例如：更新用户在线状态
          }
          break

        case WS_EVENTS.NOTIFICATION:
          // 触发全局通知
          console.log("收到通知:", message.payload)
          break

        case WS_EVENTS.CHAT_MESSAGE:
          // 处理聊天消息
          console.log("聊天消息:", message.payload)
          break
      }
    },

    // 发送消息
    async send({ commit, state }, { action, payload }) {
      // 直接从单例获取实例
      const manager = WebSocketManager.getInstance()

      if (!manager.isConnected()) {
        // 未连接时加入队列
        commit("ADD_TO_QUEUE", { action, payload }) //UI相关
        manager.queueMessage(action, payload) //执行相关
        return false
      }

      // 添加发送消息记录
      commit("ADD_OUTGOING_MESSAGE", { action, payload })


      // 发送消息
      const success = manager.send(action, payload)

      return success
    },

    // 刷新消息队列
    flushMessageQueue({ commit, state, dispatch }) {
      const queue = [...state.messages.queue]
      if (queue.length === 0) return

      commit("CLEAR_QUEUE")

      queue.forEach(msg => {
        dispatch("send", {
          type: msg.type,
          payload: msg.payload
        })
      })
    },

    // 恢复会话
    async resumeSession({ dispatch }, userId) {
      await dispatch("send", {
        type: WS_EVENTS.RESUME_SESSION,
        payload: {
          userId,
          timestamp: Date.now()
        }
      })
    },

    // 手动连接
    async connect({ dispatch }) {
      return dispatch("initialize")
    },

    // 手动断开连接
    disconnect() {
      const manager = WebSocketManager.getInstance()
      manager.disconnect(true)
    },

    // 重新连接
    reconnect() {
      const manager = WebSocketManager.getInstance()
      manager.reconnect()
    },

    // 标记消息已读
    markAsRead({ commit }) {
      commit("MARK_MESSAGES_AS_READ")
    },

    // 清空所有消息
    clearMessages({ commit }) {
      commit("CLEAR_MESSAGES")
    },

    // 更新连接配置
    updateConfig({ commit, state }, config) {
      if (config.url) {
        commit("SET_URL", config.url)
      }

      const manager = WebSocketManager.getInstance()
      manager.reconfigure(config)
    },

    // 重置所有状态
    reset({ commit }) {
      WebSocketManager.destroyInstance()
      commit("RESET_STATE")
    },

    // 从持久化存储恢复
    restore({ commit }) {
      try {
        // 恢复用户ID
        const userId = localStorage.getItem(STORAGE_KEY.USER_ID)
        if (userId) {
          commit("SET_USER_ID", userId)
        }
      } catch (error) {
        console.error("Failed to restore WebSocket state:", error)
      }
    }
  },

  getters: {
    // 连接状态
    isConnected: state => state.connection.connected,
    isReconnecting: state => state.connection.reconnecting,
    connectionState: state => {
      if (state.connection.connected) return "connected"
      if (state.connection.reconnecting) return "reconnecting"
      return "disconnected"
    },
    connectionUrl: state => state.connection.url,
    reconnectAttempts: state => state.connection.reconnectAttempts,

    // 消息相关
    messages: state => state.messages.list,
    lastMessage: state => state.messages.lastMessage,
    unreadCount: state => state.messages.unreadCount,
    hasUnread: state => state.messages.unreadCount > 0,
    messageQueue: state => state.messages.queue,
    queueLength: state => state.messages.queue.length,

    // 按类型过滤消息
    messagesByType: state => type => {
      return state.messages.list.filter(msg => msg.type === type)
    },

    // 最新消息
    latestMessages:
      state =>
        (limit = 10) => {
          return state.messages.list.slice(-limit)
        },

    // 会话信息
    sessionId: state => state.session.id,
    userId: state => state.session.userId,
    sessionStartTime: state => state.session.startTime,
    lastActive: state => state.session.lastActive,

    // 统计信息
    stats: state => state.stats,
    messagesSent: state => state.stats.messagesSent,
    messagesReceived: state => state.stats.messagesReceived,
    reconnectCount: state => state.stats.reconnects,
    errorCount: state => state.stats.errors,
    connectedTime: state => {
      if (!state.stats.connectedAt) return 0
      if (state.connection.connected) {
        return Date.now() - state.stats.connectedAt
      }
      if (state.stats.disconnectedAt) {
        return state.stats.disconnectedAt - state.stats.connectedAt
      }
      return 0
    },

    // 错误信息
    error: state => state.error,
    hasError: state => !!state.error,

    // 综合状态
    status: state => {
      if (state.connection.connected) return "online"
      if (state.connection.reconnecting) return "reconnecting"
      if (state.error) return "error"
      return "offline"
    },

    statusText: state => {
      if (state.connection.connected) return "已连接"
      if (state.connection.reconnecting) {
        return `重连中 (${state.connection.reconnectAttempts}次)`
      }
      if (state.error) return "连接错误"
      return "未连接"
    },

    statusColor: state => {
      if (state.connection.connected) return "success"
      if (state.connection.reconnecting) return "warning"
      if (state.error) return "danger"
      return "info"
    }
  }
}
